// ---------------------------------------------------------------------------
// Navigation groups config — single source of truth for:
//   • Dashboard section cards
//   • Bug-report "Section / Area" dropdown
//
// To add a new page: add one entry here and it automatically appears everywhere.
// ---------------------------------------------------------------------------

import type { JSX } from 'react';
import {
  GridView as BlocksIcon,
  Construction as PreGroundIcon,
  Agriculture as FarmTasksIcon,
  Spa as PlantingIcon,
  FilterAlt as ChemicalIcon,
  Science as FertilizerIcon,
  BugReport as PestObsIcon,
  Sanitizer as SprayFertIcon,
  WaterDrop as IrrigationTaskIcon,
  EnergySavingsLeaf as HarvestIcon,
  BarChart as ReportsIcon,
  Inventory2 as InventoryIcon,
  People as StaffIcon,
  Engineering as ContractorIcon,
  Work as TitlesIcon,
  Settings as SettingsIcon,
  Feedback as BugReportCardIcon,
  AccessTime as TimesheetIcon,
  FactCheck as ChecklistIcon,
} from '@mui/icons-material';

export interface WebNavCard {
  readonly label: string;
  readonly description: string;
  readonly icon: JSX.Element;
  readonly path: string;
  readonly color: string;
  readonly available: boolean;
}

export interface WebNavGroup {
  readonly heading: string;
  readonly items: readonly WebNavCard[];
}

export const WEB_NAV_GROUPS: readonly WebNavGroup[] = [
  {
    heading: 'Blocks',
    items: [
      { label: 'Farm Blocks', description: 'Farm blocks, plantings and crop records', icon: <BlocksIcon fontSize="large" />, path: '/blocks', color: '#2e7d32', available: true },
    ],
  },
  {
    heading: 'Farm Tasks',
    items: [
      { label: 'Chemical Template', description: 'Create Chemical Template', icon: <ChemicalIcon fontSize="large" />, path: '/chemical-templates', color: '#2e7d32', available: true },
      { label: 'Daily Check Sheets', description: 'Daily, pre and post checks with photos', icon: <ChecklistIcon fontSize="large" />, path: '/daily-check-sheets', color: '#00838f', available: true },
      { label: 'Fertilizer Template', description: 'Create Fertiliser Template', icon: <FertilizerIcon fontSize="large" />, path: '/fertilizer-templates', color: '#0277bd', available: true },
      { label: 'General Tasks', description: 'Day-to-day farm task management', icon: <FarmTasksIcon fontSize="large" />, path: '/general-tasks', color: '#0277bd', available: true },
      { label: 'Harvest', description: 'Use when harvesting', icon: <HarvestIcon fontSize="large" />, path: '/harvest-templates', color: '#2e7d32', available: true },
      { label: 'Irrigation', description: 'Use when Irrigating', icon: <IrrigationTaskIcon fontSize="large" />, path: '/irrigation-task', color: '#0277bd', available: true },
      { label: 'Pest Disease Observations', description: 'Record Observations', icon: <PestObsIcon fontSize="large" />, path: '/pest-disease-observations', color: '#e53935', available: true },
      { label: 'Planting', description: 'Use when planting', icon: <PlantingIcon fontSize="large" />, path: '/planting-templates', color: '#2e7d32', available: true },
      { label: 'Pre-Ground Works', description: 'Use for Pre-Ground Works', icon: <PreGroundIcon fontSize="large" />, path: '/pre-ground-works', color: '#0277bd', available: true },
      { label: 'Spray & Fertiliser', description: 'Used for Spraying & Fertilising', icon: <SprayFertIcon fontSize="large" />, path: '/spray-task', color: '#0277bd', available: true },
      { label: 'Timesheet', description: 'Per-labourer hours, costs and leave', icon: <TimesheetIcon fontSize="large" />, path: '/timesheet', color: '#4527a0', available: true },
    ],
  },
  {
    heading: 'Reports',
    items: [
      { label: 'Reports', description: 'Block, harvest, QA, irrigation & cost reports', icon: <ReportsIcon fontSize="large" />, path: '/reports', color: '#00695c', available: true },
    ],
  },
  {
    heading: 'Stock Inventory',
    items: [
      { label: 'Product Purchases', description: 'Product and stock inventory management', icon: <InventoryIcon fontSize="large" />, path: '/stock-inventory', color: '#e65100', available: true },
    ],
  },
  {
    heading: 'Settings',
    items: [
      { label: 'Chemical Stock', description: 'Chemical product catalogue', icon: <SettingsIcon fontSize="large" />, path: '/chemicals', color: '#c62828', available: true },
      { label: 'Company Settings', description: 'Company profile and preferences', icon: <SettingsIcon fontSize="large" />, path: '/settings', color: '#546e7a', available: true },
      { label: 'Daily Check List', description: 'Daily check items — presets plus your own', icon: <SettingsIcon fontSize="large" />, path: '/daily-check-list', color: '#00838f', available: true },
      { label: 'Fertilizers', description: 'Fertilizer product catalogue', icon: <SettingsIcon fontSize="large" />, path: '/fertilizers', color: '#2e7d32', available: true },
      { label: 'KPA / PSI', description: 'Spray pressure settings', icon: <SettingsIcon fontSize="large" />, path: '/kpa-psi', color: '#0277bd', available: true },
      { label: 'ML Hours', description: 'Irrigation flow rate settings', icon: <SettingsIcon fontSize="large" />, path: '/ml-hours', color: '#0277bd', available: true },
      { label: 'Pack Categories', description: 'Manage pack categories', icon: <SettingsIcon fontSize="large" />, path: '/pack-categories', color: '#6a1b9a', available: true },
      { label: 'Pack Count', description: 'Manage pack count types', icon: <SettingsIcon fontSize="large" />, path: '/pack-count', color: '#6a1b9a', available: true },
      { label: 'Post Check', description: 'Post check items — presets plus your own', icon: <SettingsIcon fontSize="large" />, path: '/post-check', color: '#4e342e', available: true },
      { label: 'Post-Harvest Settings', description: 'Post-harvest check configurations', icon: <SettingsIcon fontSize="large" />, path: '/post-harvest-settings', color: '#2e7d32', available: true },
      { label: 'Pre Check', description: 'Pre check items — presets plus your own', icon: <SettingsIcon fontSize="large" />, path: '/pre-check', color: '#5d4037', available: true },
      { label: 'Pre-Harvest Checks', description: 'Pre-harvest check configurations', icon: <SettingsIcon fontSize="large" />, path: '/pre-harvest-checks', color: '#2e7d32', available: true },
      { label: 'Privacy Statement', description: 'View privacy policy', icon: <SettingsIcon fontSize="large" />, path: '/privacy', color: '#546e7a', available: true },
      { label: 'Program Settings', description: 'Program reference data and configuration', icon: <SettingsIcon fontSize="large" />, path: '/program-settings', color: '#e65100', available: true },
      { label: 'Sheds / Storage', description: 'Sheds and storage facilities', icon: <SettingsIcon fontSize="large" />, path: '/sheds', color: '#546e7a', available: true },
      { label: 'Spray Tanks', description: 'Spray tank sizes', icon: <SettingsIcon fontSize="large" />, path: '/spray-tanks', color: '#0277bd', available: true },
      { label: 'Staff', description: 'Manage staff accounts and roles', icon: <StaffIcon fontSize="large" />, path: '/staff', color: '#1565c0', available: true },
      { label: 'Contractors', description: 'Contractors, their details and staff', icon: <ContractorIcon fontSize="large" />, path: '/contractors', color: '#1565c0', available: true },
      { label: 'SWMS Rankings', description: 'Risk ranking labels and colours', icon: <SettingsIcon fontSize="large" />, path: '/swms-rankings', color: '#e65100', available: true },
      { label: 'SWMS Tasks', description: 'Safe Work Method Statement tasks', icon: <SettingsIcon fontSize="large" />, path: '/swms-tasks', color: '#e65100', available: true },
      { label: 'Task Types', description: 'General task type configuration', icon: <SettingsIcon fontSize="large" />, path: '/task-types', color: '#1565c0', available: true },
      { label: 'Terms & Conditions', description: 'View terms and conditions', icon: <SettingsIcon fontSize="large" />, path: '/terms', color: '#546e7a', available: true },
      { label: 'Titles', description: 'Manage employee title definitions', icon: <TitlesIcon fontSize="large" />, path: '/titles', color: '#4527a0', available: true },
    ],
  },
  {
    heading: 'Contact',
    items: [
      { label: 'Bugs / New Features / Contact', description: 'Report a bug, request a feature or contact us', icon: <BugReportCardIcon fontSize="large" />, path: '/bug-report', color: '#d32f2f', available: true },
    ],
  },
];

// Every card's path — the ids a job title stores in user_titles.visible_cards.
// A card added above is picked up by the Job Titles editor automatically and
// starts hidden for every title until an admin grants it.
export const WEB_NAV_CARD_PATHS: readonly string[] = WEB_NAV_GROUPS.flatMap((group) => group.items.map((item) => item.path));
